import { LazoAIButton } from "@/components/app-bar-components/AppBarBody";
import { useRightDrawer } from "@/contexts/RightDrawerContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { SearchHistoryType } from "@/enums/SearchHistoryType";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { useControlNavbarColor } from "@/hooks/useControlNavbarColor";
import { useResponsive } from "@/hooks/useResponsive";
import { SearchHistory } from "@/models/SearchHistory";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { DataRoomFileSection } from "@/pages/OneDataRoomFilesPage";
import { DataRoomEntry } from "@/pages/OneDataRoomPage";
import DataRoomService from "@/services/DataRoomService";
import SearchHistoryService from "@/services/SearchHistoryService";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { Box, Collapse, IconButton, Typography, styled, typographyClasses } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CaretLeftSVG from "../../../assets/images/caret-left-logo.svg";
import FlagSVG from "../../../assets/images/flag.svg";
import SettingsSVG from "../../../assets/images/settings.svg";
import StarIconSvg from "../../../assets/images/star-logo.svg";
import { StyledInputField } from "../fundraising/advanced-search/AdvancedSearchFilters";
import { matchEntryNamesByTerm } from "./DataRoomCategoriesDefines";
import DataRoomSearchMenu from "./DataRoomSearchMenu";

const debouncer = new Debouncer(200);

interface DataRoomAppBarProps {
  makeStatic?: boolean;
  setSection?: Dispatch<SetStateAction<DataRoomFileSection>>;
  section?: DataRoomFileSection;
  title?: string;
  onClickMenuOpt?: (opt: DataRoomEntry | DataRoomFile) => void;
}

function arePropsEqual(oldProps: DataRoomAppBarProps, newProps: DataRoomAppBarProps) {
  return (
    oldProps.makeStatic === newProps.makeStatic &&
    oldProps.setSection === newProps.setSection &&
    oldProps.section === newProps.section &&
    oldProps.title === newProps.title &&
    oldProps.onClickMenuOpt === newProps.onClickMenuOpt
  );
}

function DataRoomAppBar({ makeStatic, setSection, section, title, onClickMenuOpt }: DataRoomAppBarProps) {
  const { t } = useTranslation("virtualDataRooms");
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, isDesktop } = useResponsive();

  // Control navbar background color
  useControlNavbarColor(Colors.WHITE);

  function handleSectionClick(sec: DataRoomFileSection) {
    if (!setSection) {
      navigate(LazoPaths.ONE_DATA_ROOM_FILES, {
        state: {
          section: sec,
        },
      });
    } else {
      setSection(sec);
    }
  }

  const [menuAnchor, setMenuAnchor] = useState<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [loadingOpts, setLoadingOpts] = useState<boolean>(false);
  const [menuOpts, setMenuOpts] = useState<(DataRoomFile | DataRoomEntry)[]>([]);
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    SearchHistoryService.getByUserAndType(SearchHistoryType.DATA_ROOM).then((r) => {
      r.onSuccess((c) => {
        setSearchHistory(c);
      });
    });
  }, []);

  const { currentTenantMembership } = useTenantMemberships();

  useEffect(() => {
    setLoadingOpts(true);
    debouncer.executeLastCall(() => {
      const frontendPromise = new Promise((res) => res(matchEntryNamesByTerm(inputValue)));
      const backendPromise = DataRoomService.search(inputValue, currentTenantMembership.tenant.externalId);
      Promise.all([frontendPromise, backendPromise])
        .then((v) => {
          v[1].onSuccess((page) => {
            const c = page.content;
            let frontRes = v[0] as DataRoomEntry[];
            setMenuOpts([...frontRes, ...c]);
          });
        })
        .finally(() => setLoadingOpts(false));
    });
  }, [inputValue]);

  function onFocus(e: React.FocusEvent<HTMLInputElement>) {
    setMenuAnchor(e.target);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value !== "" && !menuAnchor) {
      setMenuAnchor(e.target);
    }
    setInputValue(e.target.value);
  }

  function saveHistoryAndRedirect(opt: DataRoomEntry | DataRoomFile) {
    SearchHistoryService.save({
      search: inputValue,
      type: SearchHistoryType.DATA_ROOM,
    }).then((r) => {
      r.onSuccess((c) =>
        setSearchHistory((prev) => {
          let filtered = prev.filter((s) => s.search !== c.search);
          filtered.unshift(c);
          return filtered;
        })
      );
    });
    onClickMenuOpt(opt);
    setMenuAnchor(null);
  }

  const { isOpen, setIsOpen } = useRightDrawer();

  return (
    <>
      <DataRoomAppBarContainer makeStatic={makeStatic} isMobile={isMobile || isTablet}>
        {isMobile || isTablet ? (
          <>
            {/* Mobile Header Row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <MobileTitleContainer onClick={() => navigate(LazoPaths.ONE_DATA_ROOM)}>
                {location.pathname !== LazoPaths.ONE_DATA_ROOM && <img src={CaretLeftSVG.src} style={{ width: "20px", height: "20px" }} />}
                <Typography className="mobile-title">{title ?? t("dataRoomFiles.appBar.masterRoom")}</Typography>
              </MobileTitleContainer>
              <IconButton
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label="Open mobile menu"
                sx={{
                  minWidth: "44px",
                  minHeight: "44px",
                  touchAction: "manipulation",
                }}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>

            {/* Mobile Search Row */}
            <Box sx={{ width: "100%" }}>
              <StyledInputField
                InputProps={{
                  endAdornment: (
                    <>
                      {inputValue.trim() !== "" && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            onInputChange({ target: { value: "" } } as any);
                            setMenuAnchor(null);
                          }}
                          aria-label="Clear search"
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ),
                }}
                value={inputValue}
                hasValue={inputValue !== ""}
                onChange={onInputChange}
                onFocus={onFocus}
                onKeyDown={(ev) => {
                  if (ev.key === "Escape") {
                    // @ts-expect-error
                    ev.target.blur();
                    setMenuAnchor(null);
                    setInputValue("");
                  }
                }}
                color={Colors.LAZO_PURPLE}
                autoComplete="off"
                placeHolder={t("dataRoomFiles.appBar.search")}
                inputProps={{
                  "aria-label": "Search data room files",
                  inputMode: "search",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                }}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    minHeight: "48px",
                  },
                }}
              />
            </Box>

            {/* Mobile Action Menu (Collapsible) */}
            <Collapse in={mobileMenuOpen} sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1, pt: 1 }}>
                <MobileButtonContainer
                  current={section === DataRoomFileSection.MANAGE}
                  onClick={() => {
                    handleSectionClick(DataRoomFileSection.MANAGE);
                    setMobileMenuOpen(false);
                  }}
                >
                  <img src={SettingsSVG.src} alt="settings-svg" style={{ width: "20px", height: "20px" }} />
                  <Typography>{t("dataRoomFiles.appBar.manage")}</Typography>
                </MobileButtonContainer>

                {/*LAT-710 HIDE RED FLAGS*/}
                {false && (
                  <MobileButtonContainer
                    current={section === DataRoomFileSection.RED_FLAGS}
                    onClick={() => {
                      handleSectionClick(DataRoomFileSection.RED_FLAGS);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <img src={FlagSVG.src} alt="flag-svg" style={{ width: "20px", height: "20px" }} />
                    <Typography>{t("dataRoomFiles.appBar.redFlags")}</Typography>
                  </MobileButtonContainer>
                )}

                {!isOpen && (
                  <LazoAIButton
                    sx={{
                      padding: "0.75rem 1rem",
                      borderRadius: "0.5rem",
                      minHeight: "48px",
                      width: "100%",
                      justifyContent: "center",
                    }}
                    onClick={() => {
                      setIsOpen((prev) => !prev);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <img
                      src={StarIconSvg.src}
                      style={{
                        filter: "brightness(100)",
                        width: "16px",
                        height: "16px",
                      }}
                      alt="star"
                    />
                    <Typography textTransform="none" fontWeight={600} fontSize={"0.875rem"} color="white">
                      {t("dataRoomFiles.appBar.lazoAI")}
                    </Typography>
                  </LazoAIButton>
                )}
              </Box>
            </Collapse>
          </>
        ) : (
          /* Desktop Layout */
          <>
            <TitleContainer onClick={() => navigate(LazoPaths.ONE_DATA_ROOM)} isTablet={isTablet}>
              {location.pathname !== LazoPaths.ONE_DATA_ROOM && <img src={CaretLeftSVG.src} style={{ width: "16px", height: "16px" }} />}
              <Typography className="title">{title ?? t("dataRoomFiles.appBar.masterRoom")}</Typography>
            </TitleContainer>
            <Box className="right-container">
              <SimpleButtonContainer
                current={section === DataRoomFileSection.MANAGE}
                onClick={() => handleSectionClick(DataRoomFileSection.MANAGE)}
                isTablet={isTablet}
              >
                <img src={SettingsSVG.src} alt="settings-svg" style={{ width: "16px", height: "16px" }} />
                <Typography>{t("dataRoomFiles.appBar.manage")}</Typography>
              </SimpleButtonContainer>

              {/*LAT-710 HIDE RED FLAGS*/}
              {false && (
                <SimpleButtonContainer
                  current={section === DataRoomFileSection.RED_FLAGS}
                  onClick={() => handleSectionClick(DataRoomFileSection.RED_FLAGS)}
                  isTablet={isTablet}
                >
                  <img src={FlagSVG.src} alt="flag-svg" style={{ width: "16px", height: "16px" }} />
                  <Typography>{t("dataRoomFiles.appBar.redFlags")}</Typography>
                </SimpleButtonContainer>
              )}
              <StyledInputField
                InputProps={{
                  endAdornment: (
                    <>
                      {inputValue.trim() !== "" && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            onInputChange({ target: { value: "" } } as any);
                            setMenuAnchor(null);
                          }}
                          aria-label="Clear search"
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ),
                }}
                value={inputValue}
                hasValue={inputValue !== ""}
                onChange={onInputChange}
                onFocus={onFocus}
                onKeyDown={(ev) => {
                  if (ev.key === "Escape") {
                    // @ts-expect-error
                    ev.target.blur();
                    setMenuAnchor(null);
                    setInputValue("");
                  }
                }}
                color={Colors.LAZO_PURPLE}
                autoComplete="off"
                placeHolder={t("dataRoomFiles.appBar.search")}
                inputProps={{
                  "aria-label": "Search data room files",
                  inputMode: "search",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                }}
                sx={{
                  width: isTablet ? "18rem" : isDesktop ? "22rem" : "25rem",
                }}
              />
              {!isOpen && (
                <LazoAIButton
                  sx={{
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    maxHeight: "2.75rem",
                  }}
                  onClick={() => setIsOpen((prev) => !prev)}
                >
                  <img
                    src={StarIconSvg.src}
                    style={{
                      filter: "brightness(100)",
                      width: "16px",
                      height: "16px",
                    }}
                    alt="star"
                  />
                  <Typography textTransform="none" fontWeight={600} fontSize={"0.875rem"} color="white">
                    {t("dataRoomFiles.appBar.lazoAI")}
                  </Typography>
                </LazoAIButton>
              )}
            </Box>
          </>
        )}
      </DataRoomAppBarContainer>
      <DataRoomSearchMenu
        menuAnchor={menuAnchor}
        setMenuAnchor={setMenuAnchor}
        loading={loadingOpts}
        onClickMenuOpt={saveHistoryAndRedirect}
        menuOptions={menuOpts}
        inputValue={inputValue}
        onInputChange={onInputChange}
        searchHistory={searchHistory}
      />
    </>
  );
}

export const DataRoomAppBarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "makeStatic" && prop !== "isMobile",
})<{ makeStatic?: boolean; isMobile?: boolean }>(({ theme, makeStatic, isMobile }) => ({
  zIndex: theme.zIndex.appBar,
  display: "flex",
  flexDirection: isMobile ? "column" : "row",
  alignItems: isMobile ? "stretch" : "center",
  justifyContent: isMobile ? "flex-start" : "space-between",
  backgroundColor: Colors.WHITE,
  height: isMobile ? "auto" : "4.25rem",
  maxHeight: isMobile ? "none" : "4.25rem",
  minHeight: isMobile ? "auto" : "3rem",
  padding: isMobile ? "0.75rem 1rem" : "1rem 1rem 0.75rem 1rem",
  gap: isMobile ? "0.75rem" : 0,
  ...(!makeStatic && {
    position: "sticky",
    top: 0,
  }),
  boxShadow: "0 0 20.9px 0 rgba(0, 0, 0, 0.1)",
  "& .title": {
    fontSize: theme.breakpoints.down("sm") ? "1rem" : "1.125rem",
    fontWeight: 500,
    lineHeight: "21.6px",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  "& .mobile-title": {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: "1.5",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "200px",
  },
  "& .right-container": {
    display: "flex",
    alignItems: "center",
    gap: theme.breakpoints.down("md") ? "1rem" : "1.5rem",
  },
}));

export const SimpleButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "current" && prop !== "outlineColor" && prop !== "isTablet",
})<{ current?: boolean; outlineColor?: string; isTablet?: boolean }>(({ current, outlineColor }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  height: "1.688rem",
  gap: "0.25rem",
  [`& .${typographyClasses.root}`]: {
    fontSize: "0.75rem",
    fontWeight: "500",
    lineHeight: "18px",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  ":hover": {
    cursor: "pointer",
  },
  ...(current
    ? {
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "1px",
          backgroundColor: outlineColor ?? Colors.BLACK_151517,
          transition: "width 0.2s ease-out",
          transform: "translateX(0%)",
        },
      }
    : {
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 0,
          height: "1px",
          backgroundColor: outlineColor ?? Colors.BLACK_151517,
          transition: "width 0.2s ease-out",
          transform: "translateX(0%)",
        },
        "&:hover::after": {
          width: "calc(100%)",
        },
      }),
}));

const MobileButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "current",
})<{ current?: boolean }>(({ current }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.75rem 1rem",
  minHeight: "48px",
  borderRadius: "0.5rem",
  backgroundColor: current ? "rgba(103, 58, 183, 0.08)" : "transparent",
  transition: "background-color 0.2s ease",
  "& .MuiTypography-root": {
    fontSize: "0.875rem",
    fontWeight: current ? 600 : 500,
  },
  ":hover": {
    cursor: "pointer",
    backgroundColor: current ? "rgba(103, 58, 183, 0.12)" : "rgba(0, 0, 0, 0.04)",
  },
}));

const MobileTitleContainer = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  ":hover": {
    cursor: "pointer",
  },
});

export const TitleContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isTablet",
})<{ isTablet?: boolean }>(({ isTablet }) => ({
  position: "relative",
  display: "flex",
  maxWidth: isTablet ? "30%" : "20%",
  alignItems: "center",
  gap: "0.5rem",
  ":hover": {
    cursor: "pointer",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -10,
    left: 0,
    width: 0,
    height: "1px",
    backgroundColor: Colors.BLACK_151517,
    transition: "width 0.2s ease-out",
    transform: "translateX(0%)",
  },
  "&:hover::after": {
    width: "calc(100%)",
  },
}));

export default React.memo(DataRoomAppBar, arePropsEqual);
